import type {
  CertificateTemplate,
  Certificate,
  CertificateVerificationResult,
  CreateCertificateTemplateDto,
  UpdateCertificateTemplateDto,
  CreateFieldMappingDto,
  CertificateFieldMapping,
  CertificateGenerationLog,
  IssuedCertificatesPage,
  CertificateReissueBatch,
  StartReissueDto,
  CertificateRevisionList,
} from '@/types/certificate.types';
import { UtilsService } from '../utils';
import { authorizedAPI, unauthorizedAPI } from '@/config/axios.config';

const utils = new UtilsService();

class CertificateService {
  // Certificate Templates
  async createTemplate(
    data: CreateCertificateTemplateDto,
  ): Promise<CertificateTemplate> {
    return utils.handleApiRequest(() =>
      authorizedAPI.post('/certificate-templates', data),
    );
  }

  async getTemplates(
    processId?: string,
    formId?: string,
  ): Promise<CertificateTemplate[]> {
    const params = new URLSearchParams();
    if (processId) params.append('processId', processId);
    if (formId) params.append('formId', formId);

    return utils.handleApiRequest(() =>
      authorizedAPI.get(
        `/certificate-templates${params.toString() ? `?${params.toString()}` : ''}`,
      ),
    );
  }

  async getTemplateByProcess(
    processId: string,
  ): Promise<CertificateTemplate | null> {
    return utils.handleApiRequest(() =>
      authorizedAPI.get(`/certificate-templates/process/${processId}`),
    );
  }

  async getTemplate(id: string): Promise<CertificateTemplate> {
    return utils.handleApiRequest(() =>
      authorizedAPI.get(`/certificate-templates/${id}`),
    );
  }

  async updateTemplate(
    id: string,
    data: UpdateCertificateTemplateDto,
  ): Promise<CertificateTemplate> {
    return utils.handleApiRequest(() =>
      authorizedAPI.patch(`/certificate-templates/${id}`, data),
    );
  }

  async deleteTemplate(id: string): Promise<void> {
    return utils.handleApiRequest(() =>
      authorizedAPI.delete(`/certificate-templates/${id}`),
    );
  }

  async uploadTemplate(file: File): Promise<{ fileUrl: string; fileName: string; size: number }> {
    const formData = new FormData();
    formData.append('file', file);

    return utils.handleApiRequest(() =>
      authorizedAPI.post('/certificate-templates/upload-template', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }),
    );
  }

  // Field Mappings
  async createFieldMapping(
    data: CreateFieldMappingDto,
  ): Promise<CertificateFieldMapping> {
    return utils.handleApiRequest(() =>
      authorizedAPI.post('/certificate-field-mappings', data),
    );
  }

  async getFieldMappings(templateId: string): Promise<CertificateFieldMapping[]> {
    return utils.handleApiRequest(() =>
      authorizedAPI.get(`/certificate-field-mappings/template/${templateId}`),
    );
  }

  async updateFieldMapping(
    id: string,
    data: Partial<CreateFieldMappingDto>,
  ): Promise<CertificateFieldMapping> {
    return utils.handleApiRequest(() =>
      authorizedAPI.patch(`/certificate-field-mappings/${id}`, data),
    );
  }

  async deleteFieldMapping(id: string): Promise<void> {
    return utils.handleApiRequest(() =>
      authorizedAPI.delete(`/certificate-field-mappings/${id}`),
    );
  }

  async deleteAllFieldMappings(templateId: string): Promise<void> {
    return utils.handleApiRequest(() =>
      authorizedAPI.delete(`/certificate-field-mappings/template/${templateId}/all`),
    );
  }

  // Certificates
  async getCertificatesByApplicantProcess(
    applicantProcessId: string,
  ): Promise<Certificate[]> {
    return utils.handleApiRequest(() =>
      authorizedAPI.get(`/certificates/applicant-process/${applicantProcessId}`),
    );
  }

  async getCertificate(id: string): Promise<Certificate> {
    return utils.handleApiRequest(() =>
      authorizedAPI.get(`/certificates/${id}`),
    );
  }

  async getCertificateDownloadUrl(
    id: string,
  ): Promise<{ downloadUrl: string; expiresIn: number }> {
    return utils.handleApiRequest(() =>
      authorizedAPI.get(`/certificates/${id}/download-url`),
    );
  }

  // Generation Logs
  async getGenerationLogsByApplicantProcess(applicantProcessId: string): Promise<CertificateGenerationLog[]> {
    return utils.handleApiRequest(() =>
      authorizedAPI.get(`/certificates/generation-logs/applicant-process/${applicantProcessId}`),
    );
  }

  async getFailedGenerationLogsByProcess(processId: string): Promise<CertificateGenerationLog[]> {
    return utils.handleApiRequest(() =>
      authorizedAPI.get(`/certificates/generation-logs/process/${processId}`),
    );
  }

  async checkEligibility(applicantProcessId: string): Promise<{ eligible: boolean; hasCertificate: boolean; hasTemplate: boolean }> {
    return utils.handleApiRequest(() =>
      authorizedAPI.get(`/certificates/check-eligibility/${applicantProcessId}`),
    );
  }

  async retryGeneration(applicantProcessId: string): Promise<{ generated: boolean; certificateId?: string }> {
    return utils.handleApiRequest(() =>
      authorizedAPI.post(`/certificates/retry/applicant-process/${applicantProcessId}`),
    );
  }

  async releaseCertificate(certificateId: string): Promise<void> {
    return utils.handleApiRequest(() =>
      authorizedAPI.post(`/certificates/${certificateId}/release`),
    );
  }

  async releaseAllCertificatesForProcess(processId: string): Promise<{ count: number }> {
    return utils.handleApiRequest(() =>
      authorizedAPI.post(`/certificates/release-all/process/${processId}`),
    );
  }

  // Reissue (admin)
  async getIssuedCertificates(
    templateId: string,
    params: { page?: number; limit?: number; olderOnly?: boolean; search?: string },
  ): Promise<IssuedCertificatesPage> {
    const query = new URLSearchParams();
    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    if (params.olderOnly) query.append('olderOnly', 'true');
    if (params.search) query.append('search', params.search);
    return utils.handleApiRequest(() =>
      authorizedAPI.get(`/certificate-reissue/templates/${templateId}/certificates?${query.toString()}`),
    );
  }

  async startReissue(templateId: string, data: StartReissueDto): Promise<CertificateReissueBatch> {
    return utils.handleApiRequest(() =>
      authorizedAPI.post(`/certificate-reissue/templates/${templateId}/batches`, data),
    );
  }

  async getLatestReissueBatch(templateId: string): Promise<CertificateReissueBatch | null> {
    // A null result arrives as an empty body.
    const batch = await utils.handleApiRequest(() =>
      authorizedAPI.get(`/certificate-reissue/templates/${templateId}/batches/latest`),
    );
    return batch || null;
  }

  async getReissueBatch(batchId: string): Promise<CertificateReissueBatch> {
    return utils.handleApiRequest(() =>
      authorizedAPI.get(`/certificate-reissue/batches/${batchId}`),
    );
  }

  async retryReissueBatch(batchId: string): Promise<CertificateReissueBatch> {
    return utils.handleApiRequest(() =>
      authorizedAPI.post(`/certificate-reissue/batches/${batchId}/retry`),
    );
  }

  async getCertificateRevisions(certificateId: string): Promise<CertificateRevisionList> {
    return utils.handleApiRequest(() =>
      authorizedAPI.get(`/certificate-reissue/certificates/${certificateId}/revisions`),
    );
  }

  /** Previous PDF of a certificate as a blob (the endpoint needs the admin's token). */
  async getCertificateRevisionDocument(revisionId: string): Promise<Blob> {
    const response = await authorizedAPI.get(`/certificate-reissue/revisions/${revisionId}/document`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Verification (Public)
  async verifyCertificate(
    verificationCode: string,
  ): Promise<CertificateVerificationResult> {
    try {
      const response = await unauthorizedAPI.get(`/certificates/verify/${verificationCode}`);
      return response.data;
    } catch (error: any) {
      // Re-throw the error so it can be handled in the component
      throw error;
    }
  }

  async getVerifiedCertificateDocument(verificationCode: string): Promise<Blob> {
    const response = await unauthorizedAPI.get(
      `/certificates/verify/${encodeURIComponent(verificationCode)}/document`,
      { responseType: 'blob' },
    );
    return response.data;
  }
}

export const certificateService = new CertificateService();

